import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useFirebaseError from '../hooks/useFirebaseError';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin, loading, currentUser, needsOnboarding, restaurantId } = useAuth();
  const { getErrorMessage } = useFirebaseError();
  
  // Obtener returnTo del state (usado por AcceptInvitation y otras páginas)
  const returnTo = location.state?.returnTo;

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirigir si ya está autenticado
  useEffect(() => {
    // Solo redirigir cuando tenemos currentUser
    if (currentUser) {
      // Si hay un returnTo (ej: desde AcceptInvitation), ir ahí primero
      // Esto permite que usuarios con rol 'customer' puedan aceptar invitaciones
      if (returnTo) {
        devLog('Redirigiendo a returnTo:', returnTo);
        navigate(returnTo, { replace: true });
        return;
      }
      
      // Si no hay returnTo, seguir el flujo normal
      if (restaurantId !== undefined) {
        if (needsOnboarding) {
          navigate('/onboarding', { replace: true });
        } else if (restaurantId) {
          // Nueva estructura: /:restaurantId (sin /dashboard)
          navigate(`/${restaurantId}`, { replace: true });
        } else {
          // Si no hay restaurantId, redirigir a la raíz para que RestaurantRedirect maneje
          navigate('/', { replace: true });
        }
      }
    }
  }, [currentUser, needsOnboarding, restaurantId, navigate, returnTo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Por favor complete todos los campos.');
      return;
    }

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // La redirección se maneja automáticamente en el useEffect
        devLog('Login exitoso, redirigiendo...');
      } else {
        // Si ya viene un mensaje de error personalizado, usarlo directamente
        // Si no, traducirlo con el hook
        const errorMessage = typeof result.error === 'string'
          ? result.error
          : getErrorMessage(result.error) || 'Credenciales inválidas. Verifique email y contraseña.';
        setError(errorMessage);
      }
    } catch (err) {
      devLog('Error en login:', err);
      setError(getErrorMessage(err) || 'Error durante el login. Inténtelo nuevamente.');
    }
  };

  // Función para manejar Google Login
  const handleGoogleLogin = async () => {
    setError('');

    try {
      const result = await googleLogin();

      if (result.success) {
        devLog('Google login exitoso, redirigiendo...');
        // La redirección se maneja automáticamente en el useEffect
      } else {
        setError(getErrorMessage(result.error) || 'Error de autenticación con Google.');
      }
    } catch (err) {
      devLog('Error en Google login:', err);
      setError(getErrorMessage(err) || 'Error durante el login con Google. Inténtelo nuevamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3 flex items-center justify-center p-4 relative overflow-hidden">

      <div className="w-full max-w-md relative z-10">
        {/* Logo Container con animación de resplandor */}
        <div className="text-center mb-8">
          <div className="relative mx-auto w-48 h-48 mb-8">
            {/* Animación de resplandor detrás del logo */}
            <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-green-500/30 to-green-600/20 rounded-full animate-pulse blur-xl" style={{ background: 'radial-gradient(circle, rgba(135, 201, 94, 0.3), rgba(135, 201, 94, 0.1))' }}></div>
            <div className="absolute inset-2 bg-gradient-to-r from-green-300/10 via-green-400/20 to-green-500/10 rounded-full animate-pulse blur-lg" style={{ animationDelay: '0.5s', background: 'radial-gradient(circle, rgba(135, 201, 94, 0.2), rgba(135, 201, 94, 0.05))' }}></div>

            {/* Logo central más grande */}
            <div className="absolute inset-12 bg-gradient-to-br from-surface-3 to-surface-base rounded-full flex items-center justify-center shadow-2xl">
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
                alt="KumIA Logo"
                className="w-24 h-24 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              {/* Fallback logo más grande */}
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full items-center justify-center text-lg font-bold text-content-primary hidden">
                <span className="text-content-primary">Kum</span><span className="text-green-700 dark:text-green-200">IA</span>
              </div>
            </div>
          </div>

          {/* Título con "Kum" blanco y "IA" verde */}
          <h1 className="text-5xl font-bold mb-2">
            <span className="text-content-primary">Kum</span>
            <span className="text-kumia-green">IA</span>
          </h1>

          {/* Etiqueta de producto: diferencia el Dashboard de la app de clientes,
              que comparte el mismo diseño de login */}
          <div className="mb-3 flex justify-center">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium tracking-wide"
              style={{
                backgroundColor: 'rgba(245, 166, 35, 0.10)',
                border: '1px solid rgba(245, 166, 35, 0.4)',
                color: '#F5A623',
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#F5A623' }} aria-hidden />
              Business Dashboard
            </span>
          </div>

          <p className="text-content-secondary text-lg mb-4">
            Ecosistema gastronómico inteligente que<br />
            transforma experiencias en capital, fidelidad<br />
            cuantificable y crecimiento sostenido.
          </p>
          <h2 className="text-2xl font-semibold text-green-700 dark:text-green-400 mb-8">
            Bienvenido al Business Command Center<br />
            Dashboard
          </h2>
        </div>

        {/* Formulario de login con cometa que bordea */}
        <div className="relative">
          {/* Animación de cometa que bordea el box */}
          <div className="absolute inset-0 rounded-2xl">
            {/* Cometa animado */}
            <div className="absolute w-3 h-3 bg-green-400 rounded-full shadow-lg animate-pulse">
              <div className="w-3 h-3 bg-green-400 rounded-full blur-sm animate-ping"></div>
              <div className="absolute inset-0 w-3 h-3 bg-white/50 rounded-full animate-pulse"></div>
              {/* Animación de movimiento del cometa alrededor del box */}
              <style jsx>{`
                @keyframes comet-orbit {
                  0% { transform: translate(0, 0); }
                  25% { transform: translate(384px, 0); }
                  50% { transform: translate(384px, 400px); }
                  75% { transform: translate(0, 400px); }
                  100% { transform: translate(0, 0); }
                }
                .animate-comet {
                  animation: comet-orbit 8s linear infinite;
                }
              `}</style>
              <div className="animate-comet"></div>
            </div>

            {/* Borde animado sutil */}
            <div className="absolute inset-0 rounded-2xl border border-green-500/20 animate-pulse"></div>
            <div className="absolute inset-0 rounded-2xl border border-green-400/10 animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="bg-surface-base/80 backdrop-blur-sm shadow-2xl rounded-2xl p-8 border border-line-strong/50 relative z-10">

            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                <div className="flex">
                  <div className="text-red-700 dark:text-red-400">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Google Login Button */}
            <div className="mb-6">
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-line-active rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Iniciar sesión con Google
              </button>
            </div>

            {/* Separador */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-line-active"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface-base text-content-muted">O continúa con credenciales</span>
              </div>
            </div>

            {/* Formulario email/password */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="flex items-center text-sm font-medium text-content-secondary mb-2">
                  <svg className="w-4 h-4 mr-2 text-green-700 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-surface-3 border border-line-active rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 text-content-primary placeholder-content-muted"
                  placeholder="owner@mirestaurante.com"
                  disabled={loading}
                />
                <p className="text-xs text-green-700 dark:text-green-400 mt-1">💡 Usa tu email de propietario del restaurante</p>
              </div>

              <div>
                <label htmlFor="password" className="flex items-center text-sm font-medium text-content-secondary mb-2">
                  <svg className="w-4 h-4 mr-2 text-green-700 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-surface-3 border border-line-active rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 text-content-primary placeholder-content-muted pr-12"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-content-muted hover:text-content-secondary transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-xs text-content-muted mt-1">💡 Usa tu contraseña personal</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-content-primary py-3 px-4 rounded-lg hover:from-green-700 hover:to-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-content-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-green-700 dark:text-green-300">🔐</span> INICIAR SESIÓN
                  </>
                ) : (
                  <>
                    <span className="text-green-700 dark:text-green-300 mr-2">🔐</span> INICIAR SESIÓN
                  </>
                )}
              </button>

              {/* Link para ir al registro */}
              <div className="text-center mt-6">
                <p className="text-content-muted text-sm">
                  ¿No tienes cuenta?{' '}
                  <Link
                    to="/register"
                    className="text-green-700 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium transition-colors duration-200 hover:underline"
                  >
                    Registra tu restaurante aquí
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-content-subtle text-sm mb-4">
            Powered by <span className="text-green-700 dark:text-green-400 font-semibold">KumIA Technology</span>
          </p>
          <div className="flex justify-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;